import React, { useEffect, useState } from "react";
import ButtonLoader from "../loaders/ButtonLoader";
import { clsxm } from "../../utils/clxm/clxm";

type ButtonType = {
  text: string;
  onClick?: () => Promise<void> | void;
  isActive: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  isButtonLoading?: boolean;
  buttonColor?: string;
  borderColor?: string;
  isHollow?: boolean;
  className?: string;
  prefixText?: React.ReactNode;
};

const CustomButton = React.forwardRef<HTMLButtonElement, ButtonType>(
  (props, ref) => {
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const handleVerification = async () => {
      if (!props.onClick || isButtonDisabled) return;
      setIsButtonDisabled(true);
      try {
        await props.onClick();
      } catch (error) {
        console.error("Error occurred:", error);
      } finally {
        setIsButtonDisabled(false);
      }
    };

    useEffect(() => {
      setIsButtonDisabled(!!props.isButtonLoading);
    }, [props.isButtonLoading]);

    const activeButtonColor = props.buttonColor || "bg-bg-button-primary";
    const activeBorderColor =
      props.borderColor || "border-border-button-primary";
    const enabledSurface = props.isHollow
      ? "border-border-button-primary bg-[#FFFFFF] text-text-base shadow-none hover:bg-[#F4FBFE]"
      : `${activeButtonColor} ${activeBorderColor} text-text-base shadow-[0_1px_2px_rgba(79,186,232,0.35)] hover:brightness-95`;

    return (
      <button
        ref={ref}
        type="button"
        className={clsxm(
          "inline-flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 text-[13px] font-semibold tracking-[-0.01em] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#81d1f4]/70 focus-visible:ring-offset-1 active:translate-y-px disabled:cursor-not-allowed",
          props.isActive
            ? enabledSurface
            : "border-[#ECECEC] bg-[#F4F5F7] text-[#B0B4C3] shadow-none",
          props.className,
        )}
        onClick={props.isActive ? handleVerification : undefined}
        disabled={!props.isActive || isButtonDisabled}
      >
        {props.isButtonLoading ? (
          <ButtonLoader />
        ) : (
          <>
            {props.prefixText}
            <span>{props.text}</span>
          </>
        )}
      </button>
    );
  },
);

CustomButton.displayName = "CustomButton";

export default CustomButton;
